import {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import getBlobDuration from "get-blob-duration";
import { logger } from "@web-src/utils/logger";
import {
  AudioInfo,
  MediaPlayerContext,
  MediaPlayerContextValue,
} from "./MediaPlayerContext";

interface MediaPlayerProviderProps {
  children: ReactNode;
}

export const MediaPlayerProvider: FC<MediaPlayerProviderProps> = ({
  children,
}) => {
  const [currentAudio, setCurrentAudio] = useState<AudioInfo | null>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const animationRef = useRef<number>(undefined);

  const updateCurrentTimeAnimation = useCallback(() => {
    if (!audioRef.current) {
      animationRef.current = requestAnimationFrame(updateCurrentTimeAnimation);
      return;
    }
    const { currentTime } = audioRef.current;
    setCurrentAudio((prev) => (prev ? { ...prev, currentTime } : null));
    animationRef.current = requestAnimationFrame(updateCurrentTimeAnimation);
  }, []);

  useEffect(() => {
    if (currentAudio?.isPlaying) {
      updateCurrentTimeAnimation();
    }
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [currentAudio?.isPlaying, updateCurrentTimeAnimation]);

  const setAudioDuration = useCallback(async (src: string) => {
    try {
      const audioDuration = await getBlobDuration(src);
      setCurrentAudio((prev) =>
        prev ? { ...prev, duration: audioDuration } : null
      );
      return audioDuration;
    } catch (error) {
      logger.error("Failed to get audio duration:", error);
      return audioRef.current?.duration || 0;
    }
  }, []);

  const playAudio = useCallback(
    async (src: string, fileName: string, duration?: number) => {
      if (audioRef.current) {
        if (currentAudio?.src === src) {
          audioRef.current.play();
        } else {
          if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
          }

          audioRef.current.src = src;

          try {
            setCurrentAudio({
              src,
              fileName,
              isPlaying: true,
              isLoading: true,
              currentTime: 0,
              duration: duration || audioRef.current.duration || 0,
            });
            await audioRef.current.play();
            setCurrentAudio((prev) =>
              prev
                ? {
                    ...prev,
                    isLoading: false,
                    duration: prev.duration || audioRef.current?.duration || 0,
                  }
                : null
            );
          } catch (error) {
            logger.error("Failed to play audio:", error);
          }
        }
      }
    },
    [currentAudio]
  );

  const pauseAudio = useCallback(() => {
    if (!audioRef.current || !currentAudio) return;
    audioRef.current.pause();
    setCurrentAudio((prev) => (prev ? { ...prev, isPlaying: false } : null));
  }, [currentAudio]);

  const resumeAudio = useCallback(() => {
    if (!audioRef.current || !currentAudio) return;
    audioRef.current.play();
    setCurrentAudio((prev) => (prev ? { ...prev, isPlaying: true } : null));
  }, [currentAudio]);

  const stopAudio = useCallback(() => {
    if (!audioRef.current) return;
    audioRef.current.pause();
    audioRef.current.currentTime = 0;
    setCurrentAudio(null);
  }, []);

  const setCurrentTime = useCallback(
    (time: number) => {
      if (!audioRef.current || !currentAudio) return;
      audioRef.current.currentTime = time;
      setCurrentAudio((prev) => (prev ? { ...prev, currentTime: time } : null));
    },
    [currentAudio]
  );

  const handleLoadedMetadata = useCallback(() => {
    if (audioRef.current && currentAudio) {
      const { duration } = audioRef.current;

      if (
        duration &&
        !Number.isNaN(duration) &&
        Number.isFinite(duration) &&
        duration > 0
      ) {
        setCurrentAudio((prev) => (prev ? { ...prev, duration } : null));
      } else if (currentAudio.src) {
        setAudioDuration(currentAudio.src);
      }
    }
  }, [currentAudio, setAudioDuration]);

  const handleEnded = useCallback(() => {
    setCurrentAudio((prev) =>
      prev ? { ...prev, isPlaying: false, currentTime: 0 } : null
    );
  }, []);

  const handleError = useCallback(
    (e: React.SyntheticEvent<HTMLAudioElement>) => {
      logger.error("Audio playback error:", e);
      setCurrentAudio((prev) => (prev ? { ...prev, isPlaying: false } : null));
    },
    []
  );

  const contextValue = useMemo<MediaPlayerContextValue>(
    () => ({
      currentAudio,
      playAudio,
      pauseAudio,
      resumeAudio,
      stopAudio,
      setCurrentTime,
    }),
    [
      currentAudio,
      playAudio,
      pauseAudio,
      resumeAudio,
      stopAudio,
      setCurrentTime,
    ]
  );

  return (
    <MediaPlayerContext.Provider value={contextValue}>
      <audio
        ref={audioRef}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        onError={handleError}
        onPlay={() =>
          setCurrentAudio((prev) =>
            prev ? { ...prev, isPlaying: true } : null
          )
        }
        onPause={() =>
          setCurrentAudio((prev) =>
            prev ? { ...prev, isPlaying: false } : null
          )
        }
        onWaiting={() =>
          setCurrentAudio((prev) =>
            prev ? { ...prev, isLoading: true } : null
          )
        }
        onPlaying={() =>
          setCurrentAudio((prev) =>
            prev ? { ...prev, isLoading: false } : null
          )
        }
        onStalled={() =>
          setCurrentAudio((prev) =>
            prev ? { ...prev, isLoading: true } : null
          )
        }
        hidden
      />
      {children}
    </MediaPlayerContext.Provider>
  );
};
